import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useUser } from '../../users/providers/UserProvider';
import { getToken } from '../../users/service/localStorage';
import useAppResume from '../../native/useAppResume';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const POLL_INTERVAL_MS = 30_000; // poll every 30 s — replace with WebSocket/SSE when chat SDK is ready

// Attach the JWT explicitly rather than relying on axios.defaults, which can be
// briefly unset while the Supabase→app-token bridge runs (that race made every
// poll 401 and flood the console). Mirrors what CollectionProvider does.
//
// `quietRefusal` is passed by the three "mark read" writes: they run in the
// background, nobody tapped anything, and each swallows its own failure, so a
// refused one must not raise a toast either (B-31, useAxios.js).
const authConfig = (quietRefusal) => {
  const token = getToken();
  return token ? { headers: { 'x-auth-token': token }, quietRefusal } : null;
};

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  // Unread totals per notification type, e.g. { chat_message: 1 }. Null while
  // none are loaded — nothing fetched yet, signed out, or a server that does
  // not send the field. See the fetch below.
  unreadByType: null,
  // Every trade id with an unread update (P0 area C), or null from a server
  // that does not send it.
  unreadTradeIds: null,
  markRead: async () => {},
  markAllRead: async () => {},
  markTradeRead: async () => {},
  refresh: async () => {},
});

export const NotificationProvider = ({ children }) => {
  const { user, token } = useUser();
  // The account this provider currently serves. A reply for any other
  // account (signed out, or switched while it was in flight) is dropped:
  // it must not write that account's data into this session.
  // (Code review SEPARATION-7)
  const liveUserIdRef = useRef(user?._id ?? null);
  liveUserIdRef.current = user?._id ?? null;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadByType, setUnreadByType] = useState(null);
  const [unreadTradeIds, setUnreadTradeIds] = useState(null);
  const intervalRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    const cfg = authConfig();
    // No token yet (e.g. bridge in progress) — skip quietly, no request/no 401.
    if (!cfg) return;
    const forUser = liveUserIdRef.current;
    try {
      const { data } = await axios.get(`${apiUrl}/notifications`, cfg);
      if (liveUserIdRef.current !== forUser) return;
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount ?? 0);
      // `notifications` is a single page, so counting a type inside it
      // undercounts once that type's unread rows fall past the page boundary.
      // The server sends the real per-type totals; null (not {}) when the
      // response has none, so a consumer can tell "this server does not send
      // them" from "it sent them and this type is zero" (#29).
      setUnreadByType(data.unreadByType || null);
      setUnreadTradeIds(Array.isArray(data.unreadTradeIds) ? data.unreadTradeIds : null);
    } catch (err) {
      // If auth is rejected, stop polling so we don't flood the console and
      // hammer the backend every 30 s. The effect below owns the interval and
      // is keyed on the auth token, so the next token this session mints
      // restarts it. A genuinely revoked session never mints one — the
      // interceptor signs the user out instead — so that case stays stopped.
      const status = err?.response?.status;
      if (status === 401 || status === 403) stopPolling();
    }
    // stopPolling is stable (no deps), so this callback only changes when it
    // would actually need to — keying off `user?._id` rather than the `user`
    // object means a same-account re-render doesn't recreate it and doesn't
    // re-trigger the mount effect below. See #69.
  }, [stopPolling]);

  const userId = user?._id;

  // Poll on mount, whenever the account changes, and whenever a new token is
  // minted (P2-32).
  //
  // The token has to be a dependency because `fetchNotifications` above stops
  // the interval on 401/403. Keyed only on the account, a single expired-JWT
  // 401 killed polling for the rest of the session: the refresh that repairs
  // the 401 re-mints the token but leaves `_id` untouched, and the global
  // interceptor only ends the session for a *revoked* account — so nothing
  // ever changed a dependency again and the chat badge silently froze.
  //
  // The dependency is the token from context, not `getToken()`. localStorage
  // is not reactive, so a write there can never re-run an effect. Every path
  // that mints a token writes localStorage first and calls `setToken` second
  // (UserProvider's Supabase bridge, useHandleUsers, AuthCallbackPage), so the
  // two can only disagree inside that gap, and only with localStorage ahead —
  // meaning `authConfig()` already reads the newer token by the time this
  // re-runs. Context is what changes observably; localStorage is what is sent.
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      setUnreadCount(0);
      setUnreadByType(null);
      setUnreadTradeIds(null);
      return undefined;
    }
    fetchNotifications();
    // Deliberately NOT visibility-gated, and pinned by the tests in this
    // folder. This interval is mounted above the Router (App.js), so on every
    // route, backgrounded or not, it keeps refreshing what Footer's chat badge
    // reads — `unreadByType`, or the page it falls back to. Two states stop
    // it, both documented above: no token yet (`if (!cfg) return`) and the
    // 401/403 arm that calls `stopPolling()` until `userId`/`token` changes.
    //
    // That is why ChatPage's three hidden-tab branches disagree only in
    // REDUNDANCY, not in any outcome: the poll tick that [#299] flags for
    // returning before it refreshes costs the hidden badge nothing this
    // interval does not already fetch, because ChatPage's
    // `refreshNotifications` IS this very function (same `useCallback`
    // identity). Deliberately NOT a latency claim: a browser throttles a
    // hidden tab's timers to roughly 1/min and freezes them outright in a
    // discarded tab, so the real hidden-tab rate is worse than
    // POLL_INTERVAL_MS. That cuts symmetrically — ChatPage's own poll is a
    // setInterval in the same document — so it does not rescue the branch
    // [#299] is about; it only means the guarantee here is "eventually", not
    // "within 30s".
    intervalRef.current = setInterval(fetchNotifications, POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [userId, token, fetchNotifications, stopPolling]);

  // Coming back to the app refreshes the badges at once instead of up to 30 s
  // later (B-50). Not while the poll is stopped on a 401/403.
  useAppResume(() => {
    if (intervalRef.current) fetchNotifications();
  });

  // `notifications` is a dependency because the per-type totals below have to
  // be decremented in the bucket matching THIS notification's type, and the
  // loaded page is where that type is known. The cost is that markRead's
  // identity now changes whenever the page does; no PRODUCTION code calls
  // markRead or markAllRead today (ChatPage takes `refresh`, and
  // Footer takes `notifications` + `unreadByType` — the only two consumers).
  const markRead = useCallback(
    async (id) => {
      const cfg = authConfig(true);
      if (!cfg) return;
      try {
        await axios.put(`${apiUrl}/notifications/${id}/read`, null, cfg);
        const type = notifications.find((n) => n._id === id)?.type;
        setNotifications((prev) =>
          prev.map((n) => (n._id === id ? { ...n, read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
        // Left alone when the server sends no per-type totals — inventing a
        // map here would flip consumers onto a fallback-free path (#29).
        if (type) {
          setUnreadByType((prev) =>
            prev
              ? { ...prev, [type]: Math.max(0, (prev[type] || 0) - 1) }
              : prev
          );
        }
      } catch {}
    },
    [notifications]
  );

  const markAllRead = useCallback(async () => {
    const cfg = authConfig(true);
    if (!cfg) return;
    try {
      await axios.put(`${apiUrl}/notifications/read-all`, null, cfg);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      setUnreadByType((prev) => (prev ? {} : prev));
    } catch {}
  }, []);

  // Opening a trade marks every notification about it read (P0 area C) — the
  // Trades badge counts trades with an unread update. Local state is updated
  // optimistically for the loaded page so the badge drops at once; the
  // server's totals are then re-read, since rows past the page may have
  // changed too.
  const markTradeRead = useCallback(
    async (tradeId) => {
      const cfg = authConfig(true);
      if (!cfg || !tradeId) return;
      const id = String(tradeId);
      try {
        await axios.put(`${apiUrl}/notifications/trade/${encodeURIComponent(id)}/read`, null, cfg);
        setNotifications((prev) =>
          prev.map((n) =>
            n.relatedModel === 'TradeRequest' && String(n.relatedId) === id && !n.read
              ? { ...n, read: true }
              : n
          )
        );
        setUnreadTradeIds((prev) => (prev ? prev.filter((x) => String(x) !== id) : prev));
        await fetchNotifications();
      } catch {}
    },
    [fetchNotifications]
  );

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        unreadByType,
        unreadTradeIds,
        markRead,
        markAllRead,
        markTradeRead,
        refresh: fetchNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
